import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import { type PropsWithChildren, useState } from "react";
import { useAccount } from "wagmi";
import TokenBrandedMetamask from "~icons/token-branded/metamask";
import AztecSymbol from "../assets/aztec-symbol.svg";
import DividerDark from "../assets/divider-dark.svg";
import { useAztecWallet } from "../hooks/useAztecWallet";
import { shortHex } from "../utils";
import { ConnectWallets } from "./ConnectWallets";
import { EthWallet } from "./wallets/ethereum";

export default function AccountNavbarItem() {
  const { address } = useAccount();
  const { address: aztecAddress, isConnected: isAztecConnected } = useAztecWallet();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isEvmConnected = !!address;

  if (isEvmConnected && !isModalOpen) {
    return (
      <WalletsPopover>
        <ButtonGroup size="lg" radius="full" className="gap-1">
          <PopoverTrigger>
            <Button className="bg-[#245274] text-white font-semibold">
              <div className="inline-flex items-center gap-2">
                <TokenBrandedMetamask className="size-6" />
                <span>{shortHex(address ?? "0x")}</span>
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverTrigger>
            {isAztecConnected ? (
              <Button className="bg-[#245274] text-white font-semibold">
                <div className="inline-flex items-center gap-2">
                  <img src={AztecSymbol} alt="Aztec symbol" className="size-6" />
                  <span>{shortHex(aztecAddress?.toString() ?? "0x")}</span>
                </div>
              </Button>
            ) : (
              <Button className="bg-[#14DBC0] text-[#193448] font-semibold">Connect Aztec</Button>
            )}
          </PopoverTrigger>
        </ButtonGroup>
      </WalletsPopover>
    );
  }

  return <ConnectWallets onChange={(open) => setIsModalOpen(open)} />;
}

function WalletsPopover({ children }: PropsWithChildren) {
  return (
    <Popover
      placement="bottom"
      size="lg"
      style={{ zIndex: 50 }}
      classNames={{ content: "turnstile-popover-border p-4 pt-2" }}
    >
      {children}
      <PopoverContent as={Card} className="w-[400px]">
        <CardHeader>
          <p className="font-semibold text-lg text-foreground">My Wallets</p>
        </CardHeader>

        <img src={DividerDark} alt="Divider" className="w-full" />

        <CardBody className="flex flex-col gap-2 items-stretch">
          <EthWallet />

          <AztecWalletItem />
        </CardBody>
      </PopoverContent>
    </Popover>
  );
}

function AztecWalletItem() {
  const { address, isConnected, disconnect } = useAztecWallet();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleConnectClick = () => {
    setIsModalOpen(true);
  };

  if (isConnected && address) {
    return (
      <div className="flex items-center gap-4 bg-[#2E526C] px-4 py-8 rounded-xl">
        <img src={AztecSymbol} alt="Aztec symbol" className="size-6" />
        <div className="flex flex-col">
          <div className="font-semibold">Aztec Wallet</div>
          <div className="text-small text-[#C2C9CF]">{shortHex(address.toString())}</div>
        </div>
        <div className="flex-1" />
        <Button radius="full" size="sm" className="bg-[#245274] text-white font-semibold" onPress={disconnect}>
          Disconnect
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-4 bg-[#2E526C] px-4 py-8 rounded-xl">
        <img src={AztecSymbol} alt="Aztec symbol" className="size-6" />
        <div className="font-semibold">Aztec Wallet</div>
        <div className="flex-1" />
        <Button
          radius="full"
          size="lg"
          className="flex justify-between bg-[#14DBC0] text-[#193448] font-semibold"
          onPress={handleConnectClick}
        >
          Connect
        </Button>
      </div>
      {isModalOpen && <ConnectWallets onChange={setIsModalOpen} />}
    </>
  );
}
